import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Info } from "lucide-react";
import { validateDniNie } from "@/lib/dni";

export default function Step4Payer({ data, update, onNext, onBack }) {
  const [sameAsSolicitante, setSameAsSolicitante] = useState(false);

  useEffect(() => {
    if (!sameAsSolicitante) return;
    // Heurística española: los apellidos (paterno+materno) son las 2 últimas palabras;
    // todo lo anterior es el nombre (admite nombres compuestos, ej. "María José García López"
    // -> nombre "María José", apellidos "García López", en vez de tomar solo la 1ª palabra).
    const parts = (data.nombre_completo || "").trim().split(/\s+/).filter(Boolean);
    let nombre, apellidos;
    if (parts.length >= 3) {
      apellidos = parts.slice(-2).join(" ");
      nombre = parts.slice(0, -2).join(" ");
    } else if (parts.length === 2) {
      nombre = parts[0];
      apellidos = parts[1];
    } else {
      nombre = parts.join(" ");
      apellidos = "";
    }
    update({
      pagador_nombre: nombre,
      pagador_apellidos: apellidos,
      pagador_dni: data.dni_solicitante || "",
      pagador_domicilio: data.direccion_completa || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsSolicitante]);

  const dniValid = validateDniNie(data.pagador_dni || "");
  const canContinue =
    (data.pagador_nombre || "").trim().length > 1 &&
    (data.pagador_apellidos || "").trim().length > 1 &&
    dniValid &&
    (data.pagador_domicilio || "").trim().length > 3 &&
    (data.pagador_numero || "").trim().length > 0;

  return (
    <div>
      <h2 className="font-heading font-bold text-2xl text-municipal mb-2">Datos del pagador de la tasa de basuras</h2>
      <p className="text-gray-500 mb-6">
        Necesarios para gestionar la bonificación en la tasa de basuras acordada con el Ayuntamiento.
      </p>

      <div className="flex items-start gap-3 bg-municipal/5 border border-municipal/20 rounded-xl p-4 mb-6">
        <Checkbox
          id="same-payer"
          checked={sameAsSolicitante}
          onCheckedChange={(v) => setSameAsSolicitante(v)}
          data-testid="wizard-payer-same-checkbox"
        />
        <Label htmlFor="same-payer" className="font-normal text-sm text-gray-600 leading-relaxed cursor-pointer">
          El pagador de la tasa de basuras es la misma persona que el solicitante y vive en la misma dirección.
        </Label>
      </div>

      <div className="space-y-5 mb-6">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="mb-1.5 block">Nombre</Label>
            <Input
              value={data.pagador_nombre || ""}
              onChange={(e) => update({ pagador_nombre: e.target.value })}
              placeholder="Nombre"
              disabled={sameAsSolicitante}
              data-testid="wizard-payer-nombre-input"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Apellidos</Label>
            <Input
              value={data.pagador_apellidos || ""}
              onChange={(e) => update({ pagador_apellidos: e.target.value })}
              placeholder="Apellidos"
              disabled={sameAsSolicitante}
              data-testid="wizard-payer-apellidos-input"
            />
          </div>
        </div>
        <div>
          <Label className="mb-1.5 block">DNI / NIE del pagador</Label>
          <Input
            value={data.pagador_dni || ""}
            onChange={(e) => update({ pagador_dni: e.target.value.toUpperCase() })}
            placeholder="12345678A"
            disabled={sameAsSolicitante}
            data-testid="wizard-payer-dni-input"
          />
          {data.pagador_dni && !dniValid && (
            <p className="text-xs text-red-500 mt-1">DNI/NIE no válido</p>
          )}
        </div>
        <div>
          <Label className="mb-1.5 block">Calle del domicilio del pagador</Label>
          <Input
            value={data.pagador_domicilio || ""}
            onChange={(e) => update({ pagador_domicilio: e.target.value })}
            placeholder="Ej: Calle Mayor"
            data-testid="wizard-payer-domicilio-input"
          />
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          <div>
            <Label className="mb-1.5 block">Número</Label>
            <Input
              value={data.pagador_numero || ""}
              onChange={(e) => update({ pagador_numero: e.target.value })}
              placeholder="Nº"
              data-testid="wizard-payer-numero-input"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Portal / Piso</Label>
            <Input
              value={data.pagador_piso || ""}
              onChange={(e) => update({ pagador_piso: e.target.value })}
              placeholder="Ej: 2º B"
              data-testid="wizard-payer-piso-input"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Letra / Puerta</Label>
            <Input
              value={data.pagador_puerta || ""}
              onChange={(e) => update({ pagador_puerta: e.target.value })}
              placeholder="Ej: Dcha."
              data-testid="wizard-payer-puerta-input"
            />
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 text-xs text-gray-400 mb-8">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        Estos datos se usan exclusivamente para tramitar la bonificación en la tasa de basuras y se
        tratan conforme a nuestra política de privacidad. El portal y el piso son necesarios para que
        Hacienda pueda identificar el domicilio completo del pagador.
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="border-municipal text-municipal">Atrás</Button>
        <Button disabled={!canContinue} onClick={onNext} className="bg-municipal hover:bg-municipal-dark text-white" data-testid="wizard-step4-payer-next">
          Continuar
        </Button>
      </div>
    </div>
  );
}
